import React, { useMemo, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'moment/locale/it';

import { formatCurrency, formatDate, formatDateTime } from '../../utils/format';
import { ORDER_STATUSES, ORDER_TYPES } from '../../define/constants';
import endpoints from '../../define/endpoints';
import OrderStatusInfo from './OrderStatusInfo';
import { GIFTCARD_COVER_URL } from '../../../constants';
import Accordion, { AccordionStyles } from '../Accordion/Accordion';
import Typography, {
  TypographyStates,
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { TRANSLATIONS } from '../../../translations';

const OrderTease = ({
  order,
  buttons,
  reviewButtons,
  showMeetingPoint,
  className,
}) => {
  const productLink = useMemo(() => {
    return `${app_main_wpml.product_directory}/${order.listing && order.listing.slug
      }/`;
  }, [order.listing]);

  const [hasReviewButtons, setHasReviewButtons] = useState(false);
  const reviewButtonsRef = useRef(null);
  const coverImage =
    order.listing.coverImage && order.type !== ORDER_TYPES.giftcard
      ? order.listing.coverImage
      : GIFTCARD_COVER_URL;

  // Check if reviewButtons renders something after component mounts
  useEffect(() => {
    if (reviewButtonsRef.current) {
      const hasContent =
        reviewButtonsRef.current.children.length > 0 ||
        reviewButtonsRef.current.textContent.trim() !== '';
      setHasReviewButtons(hasContent);
    }
  }, [reviewButtons]);

  return (
    <div className={`entry ${className || ''}`}>
      <div className="entry__header">
        <div className="entry__image">
          <img
            srcSet={`
                ${endpoints.bucket}${coverImage}?smart&width=200 1x, 
                ${endpoints.bucket}${coverImage}?smart&width=400 2x,
              `}
            alt={order.listing.title}
            onError={(e) => {
              e.target.src =
                endpoints.bucket + 'images/listing-cover-placeholder.jpg';
            }}
          />
        </div>
        <div className="entry__intro">
          <div className="entry__title">
            {order.type === ORDER_TYPES.giftcard ? (
              <a href={app_main_wpml.giftcard_link}>
                {app_main_wpml.gift_card_title}
              </a>
            ) : (
              <>
                <a href={productLink}>
                  {order.listing.title}
                  {order.product.title != null &&
                    order.product.title != '' &&
                    ' - ' + order.product.title}
                </a>
              </>
            )}
          </div>
          <OrderStatusInfo order={order} />
        </div>
      </div>
      <div className="entry__data">
        <div>
          <div className="entry__label">{app_main_wpml.order_id}</div>
          <div className="entry__value">{order.id}</div>
        </div>
        <div>
          <div className="entry__label">{app_main_wpml.order_date}</div>
          <div className="entry__value">{formatDate(order.createdAt)}</div>
        </div>
        <div>
          <div className="entry__label">
            {(() => {
              switch (order.type) {
                case ORDER_TYPES.voucher:
                  return <>{app_main_wpml.expiration_date}</>;
                case ORDER_TYPES.booking:
                  return <>{app_main_wpml.activity_date}</>;
                case ORDER_TYPES.giftcard:
                  return <>{app_main_wpml.quantity}</>;
                default:
                  return null;
              }
            })()}
          </div>
          <div className="entry__value">
            {(() => {
              switch (order.type) {
                case ORDER_TYPES.voucher:
                  return formatDate(order.voucherExpiry);
                case ORDER_TYPES.booking:
                  return formatDateTime(order.bookingDatetime);
                case ORDER_TYPES.giftcard:
                  return (
                    <>
                      {order.items[0]['quantity']} x{' '}
                      {order.items[0]['price'] / order.items[0]['quantity']}€
                    </>
                  );
                default:
                  return null;
              }
            })()}
          </div>
        </div>
        <div>
          <div className="entry__label">{app_main_wpml.type}</div>
          <div className="entry__value">
            {(() => {
              switch (order.type) {
                case ORDER_TYPES.voucher:
                  return 'Voucher';
                case ORDER_TYPES.giftcard:
                  return <>{app_main_wpml.gift_card}</>;
                default:
                  return <>{app_main_wpml.reservation}</>;
              }
            })()}
          </div>
        </div>
        <div>
          <div className="entry__label">{app_main_wpml.total}</div>
          {(() => {
            switch (order.type) {
              case ORDER_TYPES.giftcard:
                return (
                  <div className="entry__value">{`${formatCurrency(
                    order.availableAmount
                  )}`}</div>
                );
              default:
                return (
                  <div className="entry__value">{`${formatCurrency(
                    order.total
                  )}`}</div>
                );
            }
          })()}
        </div>
      </div>

      {/* TODO: always hide in desktop version */}
      {showMeetingPoint && order.type !== ORDER_TYPES.giftcard && (
        <div className="entry__meeting-point">
          <Accordion
            style={AccordionStyles.info}
            label={
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights.regular}
              >
                {TRANSLATIONS.HOW_TO_GET_TO_THE_MEETING_POINT}
              </Typography>
            }
            leftIcon={
              <BetaIcon
                name={'pin'}
                color={IconColors.regular}
                size={IconSizes[16]}
              />
            }
          >
            <Typography
              classNames={'pt-2xs'}
              variant={TypographyVariants.p3}
              state={TypographyStates['neutral-light']}
            >
              {`${order.listing.meetingPoint} ${order.listing.meetingPointNotes ? '*' : ''
                }`}
            </Typography>

            {order.listing.meetingPointNotes && (
              <Typography
                classNames={'pt-2xs pb-xs'}
                variant={TypographyVariants.p4}
                state={TypographyStates['neutral-light']}
              >
                {`*${order.listing.meetingPointNotes}`}
              </Typography>
            )}

            {order.listing.meetingPointLink && (
              <a href={order.listing.meetingPointLink} target={'_blank'}>
                <Typography
                  variant={TypographyVariants.p3}
                  weight={TypographyWeights.bold}
                  state={TypographyStates['neutral-light']}
                >
                  {TRANSLATIONS.VIEW_THE_MAP}
                </Typography>
              </a>
            )}
          </Accordion>
        </div>
      )}

      <div className={`entry__cta ${hasReviewButtons ? '' : 'gap-0'}`}>
        <div className={`entry__cta__btns-${hasReviewButtons ? 'row' : 'col'}`}>
          {buttons}
        </div>
        <div ref={reviewButtonsRef}>{reviewButtons}</div>
      </div>
    </div>
  );
};

OrderTease.defaultProps = {
  onClick: () => { },
  order: {},
};

OrderTease.propTypes = {
  onClick: PropTypes.func,
  order: PropTypes.shape({
    id: PropTypes.number,
    bookingDatetime: PropTypes.instanceOf(moment),
    createdAt: PropTypes.instanceOf(moment),
    listing: PropTypes.object,
    status: PropTypes.oneOf(Object.values(ORDER_STATUSES)),
    total: PropTypes.number,
    voucherExpiry: PropTypes.instanceOf(moment),
    type: PropTypes.oneOf([
      ORDER_TYPES.voucher,
      ORDER_TYPES.booking,
      ORDER_TYPES.giftcard,
    ]),
  }),
  buttons: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
};

export default OrderTease;
